import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CorporateApi from "../../Services/Corporate";
import WalletApi from "../../Services/Wallet";

const APPROVED_STATUSES = new Set(["APPROVED", "APPROUVED", "ACTIVE", "VALIDATED", "VERIFIED"]);

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

const isApprovedStatus = (value) => APPROVED_STATUSES.has(normalizeStatus(value));

const resolveAccountStatus = (user = {}) =>
  user?.corporateDetails?.accountStatus ||
  user?.corporateDetails?.account_status ||
  "";

export default function useLewooPayWalletAccessEntreprise() {
  const userId = useSelector(
    (state) =>
      state?.AuthReducer?.userid ||
      state?.AuthReducer?.user?.id ||
      localStorage.getItem("userid") ||
      ""
  );
  const lewooPayToken = useSelector(
    (state) => state?.AuthReducer?.lewooPayToken || localStorage.getItem("lewooPayToken") || ""
  );
  const lewooPayAccessRefresh = useSelector((state) => state?.AuthReducer?.lewooPayAccessRefresh || 0);
  const [walletStatus, setWalletStatus] = useState("checking");
  const [walletLoading, setWalletLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState("");
  const [accountApproved, setAccountApproved] = useState(false);
  const [walletExists, setWalletExists] = useState(false);

  useEffect(() => {
    let isActive = true;
    const resolvedUserId = String(userId || "").trim();
    const resolvedToken = String(lewooPayToken || "").trim();

    const finishWithError = (nextStatus = "error") => {
      setAccountStatus("");
      setAccountApproved(false);
      setWalletExists(false);
      setWalletStatus(nextStatus);
    };

    const loadAccessState = async () => {
      if (!resolvedUserId) {
        if (isActive) {
          setWalletLoading(false);
          finishWithError();
        }

        return;
      }

      if (isActive) {
        setWalletLoading(true);
        setWalletStatus("checking");
      }

      try {
        const response = await CorporateApi.getCorporateDetails(resolvedUserId);
        const userDetails = response?.data?.data || response?.data || {};
        const resolvedAccountStatus = resolveAccountStatus(userDetails);
        
        const approved = isApprovedStatus(resolvedAccountStatus);

        if (!isActive) {
          return;
        }

        setAccountStatus(resolvedAccountStatus);
        setAccountApproved(approved);

        if (!approved) {
          setWalletExists(false);
          setWalletStatus("pending");
          return;
        }

        if (!resolvedToken) {
          setWalletExists(false);
          setWalletStatus("approved-needs-creation");
          return;
        }

        try {
          await WalletApi.getLewooPayMe(resolvedToken);

          if (!isActive) {
            return;
          }

          setWalletExists(true);
          setWalletStatus("ready");
        } catch (error) {
          if (!isActive) {
            return;
          }

          setWalletExists(false);
          setWalletStatus("approved-needs-creation");
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        finishWithError();
      } finally {
        if (isActive) {
          setWalletLoading(false);
        }
      }
    };

    loadAccessState();

    return () => {
      isActive = false;
    };
  }, [userId, lewooPayToken, lewooPayAccessRefresh]);

  const walletLocked = walletStatus !== "ready";
  const walletNeedsCreation = walletStatus === "approved-needs-creation";

  return {
    accountApproved,
    accountStatus,
    lewooPayToken,
    walletExists,
    walletLocked,
    walletLoading,
    walletNeedsCreation,
    walletStatus,
  };
}
